(ns ballot-scout-v2-frontend.auth
  (:require
   ["@auth0/auth0-spa-js" :as create-auth0-client :refer (Auth0Client)]
   ["jwt-decode" :as jwt-decode]
   [cljs.core.async.interop :refer-macros [<p!]]
   [cljs.reader :as read]
   [clojure.core.async :as async :refer [go]]
   [clojure.set :refer [rename-keys]]
   [clojure.walk :as walk]
   [re-frame.core :as rf])
  (:require-macros
   [ballot-scout-v2-frontend.config-helpers :refer [required-env-var]]))

(def client-id (required-env-var :auth0-client-id))
(def domain (required-env-var :auth0-domain))
(def audience (required-env-var :auth0-audience))

(defn get-auth0-client []
  (try
    (Auth0Client.
     #js {:domain domain
          :client_id client-id
          :redirect_uri (str (.. js/window -location -origin) "/login-callback")
          :scope "openid profile"
          :cacheLocation "memory"
          :audience audience})
    (catch :default e
      (println "Can't authenticate with auth0")
      (println e))))

(defonce auth0-client (get-auth0-client))

(defn admin?
  "Given a user profile, determines if a user is an admin"
  [user-profile]
  (contains? (:roles user-profile) :admin))

(defn partner-admin?
  "Given a user profile, determines if a user is a partner admin"
  [user-profile]
  (contains? (:roles user-profile) :partner-admin))

(defn do-with-token! [success-callback failure-callback]
  (-> (.getTokenSilently auth0-client)
      (.then #(success-callback %))
      (.catch #(failure-callback %))))

(rf/reg-cofx
 ::window-location
 (fn [cofx]
   (assoc cofx :window-location (.. js/window -location))))

(rf/reg-fx
 ::redirect-to-login
 (fn [redirect-uri]
   (.loginWithRedirect auth0-client #js {:appState (pr-str {:redirect-uri redirect-uri})})))

(rf/reg-event-fx
 ::login ;; routes to auth0's login page
 (fn [{:keys [db]} [_ {:keys [query-params] :as _route}]]
   (let [redirect-uri (:redirect query-params)]
     {:db (assoc-in db [:auth0 :status] :logging-in)
      ::redirect-to-login redirect-uri})))

(rf/reg-event-fx
 ::redirected-login
 [(rf/inject-cofx ::window-location)
  (rf/inject-cofx :ballot-scout-v2-frontend.routes/on-public-page?)]
 (fn [{:keys [window-location on-public-page?]} [_]]
   {::redirect-to-login
    (when-not on-public-page?
      (.-href window-location))}))

(defn- auth0-profile->app-profile [auth0-profile]
  (-> auth0-profile
      js->clj
      (rename-keys {"name" :username
                    "picture" :avatar
                    "https://ballotscout.org/partner-id" :partner-id
                    "https://ballotscout.org/jurisdiction-ids" :jurisdiction-ids
                    "https://ballotscout.org/email" :email
                    "https://ballotscout.org/roles" :roles})
      (update-in [:roles] #(map keyword %))
      (update-in [:roles] set)
      walk/keywordize-keys))

(rf/reg-fx
 ::handle-redirect-callback
 (fn [_]
   (go
     (let [auth0-client auth0-client
           callback (<p! (.handleRedirectCallback auth0-client))
           user (<p! (.getUser auth0-client))
           profile (auth0-profile->app-profile user)
           app-state (some-> callback js->clj (get "appState") read/read-string)]
       (rf/dispatch [::set-user-profile profile])
       (if-let [redirect-uri (:redirect-uri app-state)]
         (rf/dispatch [:ballot-scout-v2-frontend.routes/path-navigate redirect-uri])
         (rf/dispatch [:ballot-scout-v2-frontend.routes/navigate
                       :ballot-scout-v2-frontend.routes/index]))))))

(rf/reg-event-fx
 ::login-callback ;; this is where auth0 redirects to after a successful login
 (fn [{:keys [db]} _]
   {:db (assoc-in db [:auth0 :status] :parsing-auth0-hash)
    ::handle-redirect-callback nil}))

(rf/reg-fx
 ::logout
 (fn [return-to]
   (.logout auth0-client ;; this hits the auth0 logout endpoint
            (if return-to #js {:returnTo return-to} #js {}))))

(rf/reg-event-fx
 ::logout
 [(rf/inject-cofx ::window-location)]
 (fn [{:keys [db window-location]} _]
   {:db (assoc db :user {:logged-in? false})
    ::logout (str (.-origin window-location) "/")}))

(rf/reg-event-fx
 ::set-user-profile
 (fn [{:keys [db]} [_ user-profile]]
   (let [default-juri-id (-> user-profile :jurisdiction-ids first)]
     {:db (-> db
              (assoc-in [:user :profile] user-profile)
              (assoc-in [:auth0 :status] :profile-fetched)
              (assoc-in [:user :logged-in?] true))
      :dispatch-n (if (admin? user-profile)
                    [[:ballot-scout-v2-frontend.events/fetch-all-partners]
                     [:ballot-scout-v2-frontend.events/fetch-jurisdiction default-juri-id]]
                    [[:ballot-scout-v2-frontend.events/fetch-partner (:partner-id user-profile)]
                     [:ballot-scout-v2-frontend.events/fetch-jurisdiction default-juri-id]])})))

(defn fetch-profile-from-auth0 [success-callback failure-callback]
  (go
    (try
      (let [token (<p! (.getTokenSilently auth0-client))
            profile (auth0-profile->app-profile (jwt-decode token))]
        (rf/dispatch [::set-user-profile profile])
        (when success-callback (success-callback))
        profile)
      (catch js/Error _
        (when failure-callback
          (failure-callback))))))

(rf/reg-fx
 ::fetch-profile-from-auth0
 (fn [callback]
   (fetch-profile-from-auth0 callback callback)))

(rf/reg-event-fx
 ::fetch-profile-if-logged-in
 (fn [_ [_ callback]]
   {::fetch-profile-from-auth0 callback}))

(rf/reg-fx
 ::fetch-profile-or-login
 (fn [callback]
   (fetch-profile-from-auth0 callback #(rf/dispatch [::redirected-login]))))

(rf/reg-event-fx
 ::fetch-user-profile-if-needed-or-require-login
 [(rf/inject-cofx :ballot-scout-v2-frontend.routes/on-public-page?)]
 (fn [{:keys [on-public-page?]} [_ callback]]
   ;; if we can connect to auth0 and the page isn't public, we should attempt to
   ;; fetch the user profile from auth0. otherwise just render the public page
   ;; or the authentication failure page
   (if (and auth0-client (not on-public-page?))
     {::fetch-profile-or-login callback}
     (do (when callback (callback)) {}))))

(rf/reg-sub
 ::logged-in?
 (fn [db]
   (get-in db [:user :logged-in?])))
